import VarsityIcon from "@/assets/varsity-icon.png"
import { useIssueStarred } from "@/composables/useIssueStarred"
import { type Issue } from "@/model/tasks/issue"
import { IssueParty } from "@/model/tasks/issue-party"
import type { LoginSearchFilterConfiguration } from "@/pages/App/Staff/config"
import {
    $datetime,
    normalizeModuleConfiguration,
    type ModuleConfiguration,
    type ModuleConfigurationOptions,
} from "@/vf"
import { h, ref, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import IssueNew from "./new.vue"
import IssueShow from "./show.vue"

export type OfficeParticipant = {
    name: string
}

export type Selection = {
    issueGroups: string[]
    projectTypes: string[]
    productGroups: string[]
    projects: string[]
}

export type IssueListItem = Issue & {
    unreadMessageIds?: string[]
    officeParticipants?: OfficeParticipant[]
    starred?: boolean
}

function initials(name: string): string {
    return (
        name
            ?.split(" ")
            .map(n => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2) ?? ""
    )
}

export function createModuleConfiguration(
    apiBase?: string | null,
    filterOnlyMessages?: Ref<boolean>,
): [ModuleConfiguration, LoginSearchFilterConfiguration] {
    const i18n = useI18n()
    const { toggleStarred } = useIssueStarred((apiBase ?? "tasks/issue") + "/")
    const $config: ModuleConfigurationOptions = {}
    $config.routePrefix = () => "@Tasks.Issue"
    $config.readableName = {
        plural: "@tasks:issue.issues",
        singular: "@tasks:issue.issue",
    }
    const loginSearchFilter: LoginSearchFilterConfiguration = {
        enabled: true,
        shallowCopyTargets: [
            {
                name: "my-profile",
                id: "/my-varsity/my-profile/my-communication-tasks/issue",
            },
        ],
    }
    $config.list = {
        fields: [
            {
                name: "newIcon",
                title: "",
                type: "vnode",
                getter: item =>
                    item.unreadMessageIds.length > 0
                        ? h("i", { class: "fa-solid fa-envelope-open-text" })
                        : h("i", { style: "width: 1rem" }),
            },
            {
                name: "starred",
                title: "",
                type: "vnode",
                getter: i =>
                    h("i", {
                        class: i.starred ? "fas fa-star starred active" : "far fa-star starred inactive",
                        onClick: async event => {
                            event.stopPropagation()
                            await toggleStarred(i)
                        },
                    }),
            },
            {
                name: "owner",
                title: "@tasks:issue.owner",
                type: "vnode",
                getter: (issue: Issue) => {
                    if (issue.owner?.name) {
                        return issue.owner.name
                    }
                    if (!issue.guestEmail) {
                        return
                    }

                    // no owner: the issue belongs to someone who was not logged in or has no user account
                    return h("span", [
                        issue.guestEmail,
                        h("i", {
                            class: "fas fa-user-slash text-muted ml-1",
                            title: i18n.t("@tasks:issue.owner_without_user"),
                        }),
                    ])
                },
            },
            {
                name: "name",
                sortField: "a.name",
                title: "@tasks:issue.name",
                type: "string",
                getter: (issue: Issue) => {
                    if (!issue.name) {
                        return
                    }
                    let subtitle = null

                    if (issue.type === "ComThread" && issue.nameProps?.name) {
                        subtitle = issue.nameProps.name
                    }
                    if (issue.nameProps?.subtitle) {
                        subtitle = issue.nameProps.subtitle
                    }

                    if (subtitle) {
                        return [
                            h("div", [i18n.t(issue.name, issue.nameProps)]),
                            h(
                                "div",
                                {
                                    class: "font-slab color-primary",
                                },
                                [subtitle],
                            ),
                        ]
                    }

                    return i18n.t(issue.name, issue.nameProps) ?? issue.name
                },
            },
            {
                name: "createdAt",
                sortField: "a.createdAt",
                title: "@tasks:issue.createdAt",
                type: "datetime",
            },
            {
                name: "lastTaskAt",
                sortField: "a.lastTaskAt",
                title: "@tasks:issue.lastTaskAt",
                type: "datetime",
            },
            {
                name: "messageCount",
                sortField: "a.messageCount",
                title: "@tasks:issue.messageCount",
                type: "vnode",
                thClass: () => "text-center",
                tdClass: () => "text-center",
                getter: (issue: IssueListItem) =>
                    h(
                        "div",
                        {
                            class: "text-center",
                            title: i18n.t("@tasks:issue.messageCountHover", {
                                count: issue.messageCount,
                                unread: issue.unreadMessageIds?.length ?? 0,
                            }),
                        },
                        [
                            issue.messageCount +
                                (issue.unreadMessageIds?.length ?? 0 > 0
                                    ? " (" + issue.unreadMessageIds?.length + ")"
                                    : ""),
                        ],
                    ),
            },
            {
                name: "taskCount",
                sortField: "a.taskCount",
                title: "@tasks:issue.tasksCount",
                type: "vnode",
                thClass: () => "text-center",
                tdClass: () => "text-center",
                getter: (issue: Issue) =>
                    h(
                        "div",
                        {
                            class: "text-center",
                            title: i18n.t("@tasks:issue.tasksCountHover", {
                                count: issue.taskWithoutMessagesCount,
                                open: issue.openTaskWithoutMessagesCount ?? 0,
                            }),
                        },
                        [
                            issue.taskWithoutMessagesCount +
                                (issue.openTaskWithoutMessagesCount ?? 0 > 0
                                    ? " (" + issue.openTaskWithoutMessagesCount + ")"
                                    : ""),
                        ],
                    ),
            },
            {
                name: "state",
                sortField: "a.state",
                title: "@tasks:issue.state",
                type: "vnode",
                tdClass: () => "text-right",
                thClass: () => "text-right",
                getter: (issue: Issue) => {
                    if (issue.state === "done") {
                        return h("span", { title: $datetime(issue.closedAt) }, [
                            i18n.t(`@tasks:issue.issue_states.${issue.state}`),
                        ])
                    }

                    return i18n.t(`@tasks:issue.issue_states.${issue.state}`)
                },
            },
            {
                name: "responsibleParty",
                title: "@tasks:issue.responsible_party",
                type: "vnode",
                tdClass: () => "text-left",
                thClass: () => "text-left",
                getter: (issue: IssueListItem) => {
                    // Render the responsible party icon
                    let icon
                    switch (issue.responsibleParty) {
                        case IssueParty.Customer:
                            icon = h("i", {
                                class: "fas fa-user",
                                title: i18n.t("@tasks:issue.responsible_party.tooltip.customer"),
                            })
                            break
                        case IssueParty.Office:
                            icon = h("img", {
                                src: VarsityIcon,
                                class: "size-1-5",
                                title: i18n.t("@tasks:issue.responsible_party.tooltip.office"),
                            })
                            break
                        case IssueParty.Both:
                            icon = h("i", {
                                class: "fas fa-people-arrows",
                                title: i18n.t("@tasks:issue.responsible_party.tooltip.both"),
                            })
                            break
                        case IssueParty.None:
                            icon = h("i")
                            break
                    }

                    // Render office participant avatars
                    const participants = issue.officeParticipants ?? []
                    const maxAvatars = 3
                    const displayedParticipants = participants.slice(0, maxAvatars)
                    const remainingCount = participants.length - maxAvatars

                    const avatars = displayedParticipants.map(p =>
                        h(
                            "div",
                            {
                                class: "rounded-circle bg-primary d-inline-flex align-items-center justify-content-center",
                                style: "width: 1.5rem; height: 1.5rem; font-size: 0.6rem; margin-left: 0.25rem;",
                                title: p.name,
                            },
                            h("span", { class: "text-white" }, initials(p.name)),
                        ),
                    )

                    // Add overflow indicator if there are more than maxAvatars
                    if (remainingCount > 0) {
                        avatars.push(
                            h(
                                "span",
                                {
                                    class: "text-muted",
                                    style: "font-size: 0.75rem; margin-left: 0.25rem;",
                                },
                                `+${remainingCount}`,
                            ),
                        )
                    }

                    return h("div", { class: "d-flex align-items-center justify-content-start" }, [icon, ...avatars])
                },
            },
        ],
        orderField: "lastTaskAt",
        orderDirection: "desc",
        onClick: "show",
        requestParams: ref({
            viewMode: "admin",
        }),
        trClass: (issue: IssueListItem) => {
            return (issue.unreadMessageIds?.length ?? 0) > 0 ? "font-weight-bold" : ""
        },
    }
    $config.safeDeleteDialog = {
        active: true,
    }
    $config.afterSaveAction = "navigate-list"

    $config.apiBase = () => apiBase ?? "tasks/issue/"
    $config.mode = "in-table"
    $config.show = (item: Issue) => ({
        component: IssueShow,
        props: {
            id: item.id,
            viewMode: "admin",
            filterOnlyMessages: filterOnlyMessages?.value ?? false,
        },
    })
    $config.new = IssueNew

    return [normalizeModuleConfiguration($config), loginSearchFilter]
}
